import {BerwowEvent, TrackingEvent, emit, QuoteOrigin, normalizeReportBerRatings} from '@/helpers';
import {ResponseError, ApplyRecommendationsRequest, MenuDisplayOption} from '@/types';
import { ApplyRecommendationsReport } from '@/types';
import { GammaAPIService } from '@/service';
import { defineStore, storeToRefs } from 'pinia';
import { RecommendationGroup, Recommendation, RecommendationSelected, RequestRecommendationsResponse, RecommendationsEvent } from '@/types/recommendations';
import { useLoan } from './loan';
import { RequestRecommendationsRequest } from '@/types';
import { useSelections } from './selections';
import { useSettings } from './settings';
import { cloneDeep } from 'lodash';

interface RecommendationsState {
  recommendationGroups: RecommendationGroup[];
  initialRecommendationGroups: RecommendationGroup[];
  report: ApplyRecommendationsReport | undefined;
  isLoadingReport: boolean;
  isListLoading: boolean;
  berwowId: string | undefined;
  expandedRows: RecommendationGroup[];
  expandedInfos: RecommendationGroup[];
  recommendationsError: ResponseError | undefined;
}

export const useRecommendations = defineStore('recommendations', {
  state: (): RecommendationsState => ({
    recommendationGroups: [],
    initialRecommendationGroups: [],
    report: undefined,
    isLoadingReport: false,
    isListLoading: false,
    berwowId: undefined,
    expandedRows: [],
    expandedInfos: [],
    recommendationsError: undefined,
  }),
  getters: {
    groupDictionary: (state) => state.recommendationGroups.reduce((x, y) => x.set(y.id, y), new Map<string, RecommendationGroup>()),
    recommendationsSelected: (state): RecommendationSelected[] => state.recommendationGroups
      .filter(x => x.isSelected)
      .flatMap(x => x.recommendations)
      .filter(x => x.isSelected)
      .map(x => ({
        RecommendationId: x.id,
        AlreadyUpgraded: x.isUpgraded != undefined ? x.isUpgraded : false
      })),
    recommendationsPageLoading: (state) => state.recommendationGroups && state.recommendationGroups.length == 0,
    expanded: (state) => [...new Set([...state.expandedRows, ...state.expandedInfos])],
    grantMessages: (state) => state.report?.grantMessages,
    allGroupsSelected: (state) => state.recommendationGroups.every((group) => group.isSelected),
  },
  actions: {
    async queryRecommendations(options: RequestRecommendationsRequest) {
      this.isListLoading = true;
      const response = await GammaAPIService.getRecommendations(options);
      if (response && response.exceptionType) {
        emit(BerwowEvent.RecommendationsError, response);
        throw new Error(typeof response === 'object' ? JSON.stringify(response) : response);
      }
      this.setRecommendationsInfo(response, true);
      this.isListLoading = false;
    },

    setRecommendationsInfo(response: RequestRecommendationsResponse, saveInitial?: boolean) {
      this.berwowId = response.token;

      this.recommendationGroups = response.recommendationGroups.map((recomGroup: RecommendationGroup) => {
        recomGroup.isSelected = recomGroup.isSelectedOnLoad;
        recomGroup.recommendations = recomGroup.recommendations.map(r => {
          const info = response.recommendationsInfo.find((info: any) => info.id === r.id);
          if (info) {
            r.info = {
              name: info.name,
              saving: info.saving,
              comfort: info.comfort,
              price: info.price,
            };
          }
          return r;
        });
        recomGroup.recommendations.filter(r => !r.isExclusive).forEach(r => r.isSelected = true);
        const exclusive = recomGroup.recommendations.filter(r => r.isExclusive);
        const selectedExculsive = exclusive.find(r => r.isSelected);
        if (selectedExculsive) {
          selectedExculsive.isSelected = true;
        } else if (exclusive[0]) {
          exclusive[0].isSelected = true;
        }
        if (recomGroup.isSelected) {
          recomGroup.recommendations.filter(r => r.isSelected).forEach(r => r.isSelected = true);
        }
        return recomGroup;
      });

      if (saveInitial) this.initialRecommendationGroups = cloneDeep(this.recommendationGroups);
    },

    emitRecommendationsReportResult(addRecommendations: boolean, eventName: string) {
      if (this.report) {
        const { clientSessionId } = storeToRefs(useSettings());
        const eventData: RecommendationsEvent = {
          estimatedcost_netprojectcost: this.report.estimatedTotalCost,
          estimatedcost_project: this.report.totalMeasureCost,
          estimatedcost_seai_Grant: this.report.totalGrantCost,
          estimatedcost_sse_discount: this.report.totalDiscountCost,
          event: eventName,
          timestamp: Date.now(),
          clientSessionId: clientSessionId.value,
        };
        this.recommendationGroups.forEach(group => {
          eventData[group.name] = addRecommendations ? {
            description: group.description,
            selected: group.isSelectedOnLoad,
            recommendations: group.recommendations.map((recommendation) => {
              return {
                description: recommendation.description,
                selected: !!recommendation.isSelected,
                upgraded: !!recommendation.isUpgraded,
              };
            })
          } : {
            description: group.description,
            selected: group.isSelectedOnLoad
          };
        });

        emit(BerwowEvent.TrackingEvent, eventData);
      }
    },

    async applyRecommendations(updateLoanAmount = true, firstRequest?: boolean) {
      const loan = useLoan();
      const {
        loanOptions,
        selectedLoan,
        mortgageActive,
        mortgage,
        selectedLoanOption,
      } = storeToRefs(loan);
      this.isLoadingReport = true;
      let req: ApplyRecommendationsRequest = {
        BerWowId: this.berwowId,
        RecommendationsSelected: this.recommendationsSelected,
        LoanOptions: loanOptions.value.slice(1), //Remove "no loan"
        LoanOptionSelected: selectedLoan.value ? selectedLoan.value : undefined,
        PropertyValue: 0,
      };
      if (mortgageActive.value) req = { ...req, MortgageOptionSelected: mortgage.value };
      this.report = await GammaAPIService.applyRecommendations(req);
      /* TODO: REMOVE - Only for demo*/
      const md = useSettings().settings?.recommendationsPageOptions?.menuDisplay
      if (this.report && md && Array.isArray(md) && md.find((i) => i === MenuDisplayOption.BRPassport))
        this.report.afterRating = {value: 0, code: 'A1'}
      normalizeReportBerRatings(this.report);

      if (updateLoanAmount && this.report) {
        let amount = this.report?.estimatedTotalCost;
        if (selectedLoanOption.value?.maxAmountOptions?.includingGrants) amount += this.report?.totalGrantCost;
        if (selectedLoanOption.value?.maxAmountOptions?.includingDiscounts) amount += this.report?.totalDiscountCost;
        await loan.setAllLoanAmounts(amount, firstRequest);
      }

      if (firstRequest) {
        this.emitRecommendationsReportResult(false, TrackingEvent.RecommendationsReportResult);
      }
      this.isLoadingReport = false;
    },

    async quoteRecommendations(origin: QuoteOrigin) {
      const { loanOptions, selectedLoan } = storeToRefs(useLoan());
      const { mprn, addressInfo } = storeToRefs(useSelections());
      const id = (await GammaAPIService.quoteRecommendations({
        berWowId: this.berwowId,
        recommendationsSelected: this.recommendationsSelected,
        loanOptions: loanOptions.value.slice(1), //Remove "no loan"
        loanOptionSelected: selectedLoan.value && selectedLoan.value.amount ? selectedLoan.value : undefined
      })).berWowId;
      emit(BerwowEvent.Quote, {
        berWowId: id,
        address: addressInfo.value,
        MPRN: mprn.value,
        loanOptions: selectedLoan.value,
        origin
      });
      this.emitRecommendationsReportResult(true, TrackingEvent.RecommendationsOnSubmit);
    },

    expandRecommendation(recommendation: RecommendationGroup | undefined, expanded: boolean) {
      if (!recommendation) return;

      if (expanded && !this.expandedRows.find((x) => x.id === recommendation.id)) {
        this.expandedRows = [...this.expandedRows, recommendation];
      } else if (!expanded) {
        this.expandedRows = this.expandedRows.filter(
          (x) => x.id !== recommendation.id
        );
      }
    },
    expandInfo(recommendation: RecommendationGroup, expanded: boolean) {
      if (!recommendation) return;

      if (expanded && !this.expandedInfos.find((x) => x.id === recommendation.id)) {
        this.expandedInfos = [...this.expandedInfos, recommendation];
      } else if (!expanded) {
        this.expandedInfos = this.expandedInfos.filter(
          (x) => x.id !== recommendation.id
        );
      }
    },

    async toggleGroupSelected(groupId: string) {
      const group = this.groupDictionary.get(groupId);
      if (!group) return;

      group.isSelected = !group.isSelected;

      await this.applyRecommendations();
      this.emitRecommendationsReportResult(false, TrackingEvent.UserChangedRecommendations);
    },

    async setRecommendationIsSelected(groupId: string, id: string, val: boolean) {
      const group = this.groupDictionary.get(groupId);
      const recommendation = group?.recommendations.find(x => x.id === id);

      if (!group || !recommendation) return;

      if (recommendation.isExclusive) {
        group.recommendations
          .filter(x => x.isExclusive)
          .forEach(x => x.isSelected = x.id === recommendation.id);
      }
      else {
        recommendation.isSelected = val;
      }

      await this.applyRecommendations();
      this.emitRecommendationsReportResult(false, TrackingEvent.UserChangedRecommendations);
    },

    async updateRecommendationUpgraded(recommendation: Recommendation) {
      recommendation.isUpgraded = recommendation.isUpgraded != undefined ? !recommendation.isUpgraded : true;
      if (recommendation.isUpgraded) {
        await this.setRecommendationIsSelected(recommendation.groupId, recommendation.id, true);
      }
      const selected = this.recommendationsSelected.find(r => r.RecommendationId == recommendation.id);
      if (selected) {
        selected.AlreadyUpgraded = recommendation.isUpgraded != undefined ? recommendation.isUpgraded : false;
        await this.applyRecommendations();
        this.emitRecommendationsReportResult(false, TrackingEvent.UserChangedRecommendations);
      }
    },

    async loadRecommendationsById(id: string) {
      this.isListLoading = true;
      this.isLoadingReport = true;
      const recommendations = await GammaAPIService.getRecommendations({ id, idType: 2 });
      const info = await GammaAPIService.getQuotedRecommendations({ BerWowId: id, timestamp: Date.now() });

      this.report = info.recommendationsReport;
      /* TODO: REMOVE - Only for demo*/
      const md = useSettings().settings?.recommendationsPageOptions?.menuDisplay
      if (md && Array.isArray(md) && md.find((i) => i === MenuDisplayOption.BRPassport))
        this.report.afterRating = {value: 0, code: 'A1'}
      normalizeReportBerRatings(this.report);

      recommendations.recommendationGroups.forEach((group: RecommendationGroup) => {
        let hasSelectedRecommendation = false;

        group.recommendations.forEach((recommendation) => {
          recommendation.isUpgraded = info.alreadyAppliedRecommendations.includes(recommendation.id);
          recommendation.isSelected = info.recommendationIds.includes(recommendation.id) || recommendation.isUpgraded;

          if (recommendation.isSelected) {
            hasSelectedRecommendation = true;
          }
        });

        group.isSelectedOnLoad = hasSelectedRecommendation;
      });
      this.setRecommendationsInfo(recommendations, true);
      this.isListLoading = false;

      const { addLoanDataById } = useLoan();
      const loanOpts = info.request.loanOptions ? info.request.loanOptions : useSettings().settings?.loanOptions;
      await addLoanDataById(loanOpts ? loanOpts : [], info.request.loanOptionSelected);

      this.isLoadingReport = false;
    },


    isExpandedGroup(group: RecommendationGroup) {
      return this.expandedRows.some((rg: RecommendationGroup) => rg.id === group.id);
    },
    isExpandedInfo(group: RecommendationGroup) {
      return this.expandedInfos.some((rg: RecommendationGroup) => rg.id === group.id);
    },

    async resetRecommendations() {
      this.recommendationGroups = cloneDeep(this.initialRecommendationGroups);
      await this.applyRecommendations();
    }
  }
});
